import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/components/button';
import { Textarea } from '@/shared/ui/components/textarea';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import {
  useStandardPassVisits,
  useUndoStandardPassVisit,
  type StandardPassVisit,
} from './standardPassVisits';
import { moment, windowTime, type VisitFormat } from './visitFormat';

/**
 * A customer's Standard Pass visits at the Venue (ADR 0072 §8): Pass and variant, guests and window.
 * Staff with `standard_pass.visit_undo` undo a whole visit with a required reason, at any time; the
 * backend 403 stays the authoritative gate.
 */
export function StandardPassVisitsPanel({
  venueId,
  customerId,
  format,
}: {
  venueId: string;
  customerId: string;
  format: VisitFormat;
}) {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const visits = useStandardPassVisits(venueId, customerId, true);
  const undo = useUndoStandardPassVisit(venueId, customerId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const canUndo = auth.can('standard_pass.visit_undo', { kind: 'venue', venueId });

  function open(visit: StandardPassVisit) {
    setEditingId(visit.id);
    setReason('');
    undo.reset();
  }

  function close() {
    setEditingId(null);
  }

  function submit(event: FormEvent, visit: StandardPassVisit) {
    event.preventDefault();
    undo.mutate({ visitId: visit.id, reason: reason.trim() });
  }

  if (visits.isPending)
    return (
      <p className="text-sm text-muted-foreground">{t('mvpCustomers.standardVisits.loading')}</p>
    );
  if (visits.isError)
    return (
      <p role="alert" className="text-sm text-destructive">
        {t('mvpCustomers.standardVisits.loadFailed')}
      </p>
    );
  if (visits.data.length === 0)
    return (
      <p className="text-sm text-muted-foreground">{t('mvpCustomers.standardVisits.empty')}</p>
    );

  return (
    <ol className="grid gap-3">
      {visits.data.map((visit) => (
        <li key={visit.id} aria-label={visit.passName} className="rounded-lg border p-4">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <p className="font-medium">
                {t('mvpCustomers.standardVisits.passVariant', {
                  pass: visit.passName,
                  variant: visit.variantName,
                })}
              </p>
              <p className="text-sm text-muted-foreground">
                {moment(visit.acceptedAt, format)}
                {' · '}
                {t('mvpCustomers.standardVisits.guests', { count: visit.guests })}
                {' · '}
                {t('mvpCustomers.standardVisits.window', {
                  start: windowTime(visit.windowStartsAt, format),
                  end: windowTime(visit.windowEndsAt, format),
                })}
              </p>
            </div>
            {visit.undo ? (
              <span className="text-sm font-medium text-amber-700">
                {t('mvpCustomers.standardVisits.undone')}
              </span>
            ) : null}
          </div>
          {visit.undo ? (
            <p className="mt-3 text-sm text-muted-foreground">
              {t('mvpCustomers.standardVisits.undoStamp', {
                time: moment(visit.undo.undoneAt, format),
                reason: visit.undo.reason,
                by: visit.undo.undoneBy ?? t('mvpCustomers.standardVisits.unknownOperator'),
              })}
            </p>
          ) : null}
          {canUndo && !visit.undo ? (
            editingId === visit.id ? (
              <form className="mt-3 grid gap-2" onSubmit={(event) => submit(event, visit)}>
                <label htmlFor={`standard-undo-${visit.id}`} className="text-sm font-medium">
                  {t('mvpCustomers.standardVisits.reason')}
                </label>
                <Textarea
                  id={`standard-undo-${visit.id}`}
                  value={reason}
                  required
                  onChange={(event) => setReason(event.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  {t('mvpCustomers.standardVisits.noMoneyBack')}
                </p>
                {undo.isError ? (
                  <p role="alert" className="text-sm text-destructive">
                    {t('mvpCustomers.standardVisits.undoFailed')}
                  </p>
                ) : null}
                <div className="flex gap-2">
                  <Button type="submit" disabled={undo.isPending || !reason.trim()}>
                    {t('mvpCustomers.standardVisits.confirmUndo')}
                  </Button>
                  <Button type="button" variant="outline" onClick={close}>
                    {t('mvpCustomers.standardVisits.cancel')}
                  </Button>
                </div>
              </form>
            ) : (
              <Button className="mt-3" variant="outline" onClick={() => open(visit)}>
                {t('mvpCustomers.standardVisits.undo')}
              </Button>
            )
          ) : null}
        </li>
      ))}
    </ol>
  );
}
